import { useEffect } from "react";
import { listen } from "./listen.ts";

/** For when you want to do something without breaking the focus of the active element */
export const useClickByHoverToAvoidChangingFocus = (
  element: HTMLElement | null | undefined,
  callback: null | undefined | ((event: React.MouseEvent) => void),
  requireAlt = false,
): Exclude<typeof callback, null> => {
  useEffect(() => {
    if (!element || !callback) return;

    let openTimeout: (Animation | number | ReturnType<typeof setTimeout>)[] = [];
    const DURATION = 1000;

    // Create an anticipation animation that builds up to opening
    const anticipateOpen = () =>
      element.animate(
        [
          { transform: "scale(1)", offset: 0 },
          { transform: "scale(1.02)", offset: 0.4 },
          { transform: "scale(1.04)", offset: 0.6 },
          { transform: "scale(1.05)", offset: 0.95 },
          { transform: "scale(1)", offset: 1 },
        ],
        {
          duration: DURATION,
          easing: "ease-in",
          fill: "forwards",
        },
      );

    const unsub = listen(element, "mouseenter", (event) => {
      // we need anticipated animation if our click would otherwise change the focus
      if (document.activeElement == null || document.activeElement === document.body || (requireAlt && !event.altKey))
        return;
      const animation = anticipateOpen();
      openTimeout = [setTimeout(callback, DURATION, event), animation];
    });

    const unsub2 = listen(element, "mouseleave", () => {
      for (const t of openTimeout) {
        if (t instanceof Animation) t.cancel();
        else clearTimeout(t);
      }
    });

    const unsub3 = listen(
      element,
      "click",
      () => {
        for (const t of openTimeout) {
          if (t instanceof Animation) t.cancel();
          else clearTimeout(t);
        }
      },
      { capture: true, passive: true },
    );

    return () => {
      unsub();
      unsub2();
      unsub3();
    };
  }, [element, callback, requireAlt]);

  return callback ?? undefined;
};
